/**
 * THE ACTIVE-FILTER CHIP ROW.
 *
 * A narrowed screen looks exactly like a quiet week. That is the failure behind
 * more than one report on the screens this replaces — a feed showing three rows
 * because a filter was set is indistinguishable from a feed showing three rows
 * because three things happened.
 *
 * So every narrowing is visible, every chip removes exactly its own narrowing,
 * and `Clear all` returns to "everything in this window" without touching the
 * chrome controls the reader can already see set.
 */
import React from 'react';
import { DashboardFilter, FilterMeta } from '../state/filter-model';
import { chipsFor } from '../state/filter-chips';
import { hasAnyNarrowing } from '../state/filter-reducer';
import {
  ChipBar,
  ChipX,
  ClearAll,
  FilterChipButton,
  ResultCount,
} from '../dashboard-screen.styled';

export interface FilterChipRowProps {
  filter: DashboardFilter;
  meta?: FilterMeta;
  onRemove: (patch: Partial<DashboardFilter>) => void;
  onClearAll: () => void;
  /** "142 records", and the comparison count beside it when there is one.
   *  `pageOnly` is set when the figure is one page of a longer result. */
  count?: {
    total: number;
    capped?: boolean;
    compare?: number | null;
    /** The figure is one page of a longer result, not the result size. */
    pageOnly?: boolean;
  } | null;
}

export const FilterChipRow: React.FC<FilterChipRowProps> = ({
  filter,
  meta,
  onRemove,
  onClearAll,
  count,
}) => {
  const chips = chipsFor(filter, meta);
  const any = hasAnyNarrowing(filter);

  if (!chips.length && !count) return null;

  return (
    <ChipBar role="group" aria-label="Filters applied">
      {chips.map(chip => (
        <FilterChipButton
          key={chip.id}
          type="button"
          data-testid={`chip-${chip.id}`}
          aria-label={`Remove filter ${chip.label}: ${chip.value}`}
          onClick={() => onRemove(chip.patch)}
        >
          <span>
            {chip.label}: <b>{chip.value}</b>
          </span>
          <ChipX aria-hidden="true">×</ChipX>
        </FilterChipButton>
      ))}

      {any ? (
        <ClearAll type="button" onClick={onClearAll} data-testid="clear-all">
          Clear all
        </ClearAll>
      ) : null}

      {count ? (
        <ResultCount data-testid="result-count">
          {count.total.toLocaleString()}
          {count.capped ? '+' : ''}{' '}
          {count.total === 1 ? 'record' : 'records'}
          {/* A full page means there are almost certainly more behind it, and
              the response carries no total to report. Saying "on this page" is
              the difference between a bounded claim and a wrong one. */}
          {count.pageOnly ? ' on this page' : ''}
          {count.compare != null
            ? ` · ${count.compare.toLocaleString()} in the comparison period`
            : ''}
        </ResultCount>
      ) : null}
    </ChipBar>
  );
};
